import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { ErrorHandler, ScrollBarContainer } from '@playbookux/playbook-web-core';
import useFolders from '../../hooks/use-folders';

import {
  Container,
  ContainerInputSearch,
  InputSearch,
  ContainerScroll,
  TextPrimaryBold,
  Radio,
  ContainerRadio,
  ContainerLink,
  LinkManageFolders,
  Text,
} from './styles';

const FolderSearch = (props) => {
  const {
    handleChange,
    selectedFolder,
    companyId,
    project,
  } = props;

  const {
    company: { id: projectCompanyId },
    folder: { id: projectFolderId },
  } = project || {};

  const {
    groupedFolders = [],
    loading,
    error,
  } = useFolders(companyId);

  const [folderSearch, setFolderSearch] = useState(groupedFolders);
  const [search, setSearch] = useState('');

  const folderDefault = useMemo(() => {
    if (_.isEmpty(groupedFolders)) return {};

    const folderUngrouped = _.filter(groupedFolders, (folderGroup) => folderGroup.isUngrouped);
    const defaultFolder = _.filter(_.head(folderUngrouped).folders, (folder) => folder.isDefault);
    return _.head(defaultFolder);
  }, [groupedFolders]);

  const onClear = useCallback(() => {
    setFolderSearch(groupedFolders);
    setSearch('');
  }, [groupedFolders]);

  const handleSearchChange = useCallback((e, { value }) => {
    setSearch(value);

    const newFolderGrouped = [];
    _.forEach(groupedFolders, (folderGroup) => {
      const folders = _.filter(folderGroup.folders, (folder) => (
        folder.name.toLowerCase().includes(value.toLowerCase())
      ));

      if (!_.isEmpty(folders)) {
        newFolderGrouped.push({ ...folderGroup, folders });
      }

      if (_.isEmpty(folders) && folderGroup.name.toLowerCase().includes(value.toLowerCase())) {
        newFolderGrouped.push({ ...folderGroup, folders: folderGroup.folders });
      }
    });

    setFolderSearch(newFolderGrouped);
  }, [groupedFolders]);

  useEffect(() => {
    if (!_.isEmpty(groupedFolders)) {
      setFolderSearch(groupedFolders);
    }

    if (companyId !== projectCompanyId) {
      handleChange(folderDefault.id);
    } else {
      handleChange(projectFolderId);
    }
  }, [companyId, folderDefault.id, groupedFolders, handleChange, projectCompanyId, projectFolderId]);

  return (
    <Container>

      <ContainerLink>
        <Text>
          Folder
        </Text>
        <LinkManageFolders
          href="/manage-folders"
        >
          Manage Folders
        </LinkManageFolders>
      </ContainerLink>

      {error && (
        <ErrorHandler
          error={error}
          defaultMessage="Error while retrieving folders."
        />
      )}

      {!error && (
        <>
          <ContainerInputSearch>
            <InputSearch
              loading={loading}
              icon="search"
              placeholder="Search folders"
              input={{ icon: 'search', iconPosition: 'left' }}
              onSearchChange={handleSearchChange}
              value={search}
              onClear={onClear}
              showClear={search}
            />
          </ContainerInputSearch>

          <ContainerScroll>
            <ScrollBarContainer borderless>
              {folderSearch && _.map(folderSearch, (folderGroup) => (
                <>
                  <TextPrimaryBold key={folderGroup.id}>
                    {folderGroup.name}
                  </TextPrimaryBold>

                  {_.map(folderGroup.folders, (folder) => (
                    <ContainerRadio>
                      <Radio
                        key={folder.id}
                        label={folder.name}
                        name="radioGroup"
                        value={folder.id}
                        checked={folder.id === selectedFolder}
                        onChange={(e, { value }) => { handleChange(value); }}
                      />
                    </ContainerRadio>
                  ))}
                </>
              ))}

            </ScrollBarContainer>
          </ContainerScroll>
        </>
      )}

    </Container>
  );
};

FolderSearch.propTypes = {
  handleChange: PropTypes.func.isRequired,
  selectedFolder: PropTypes.string.isRequired,
  companyId: PropTypes.string.isRequired,
  project: PropTypes.shape({
    folder: PropTypes.shape({
      id: PropTypes.string,
    }),
    company: PropTypes.shape({
      id: PropTypes.string,
    }),
    companyId: PropTypes.string,
  }).isRequired,
};

export default FolderSearch;
